// src/views/examples/AddReportCustomer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Table,
} from "reactstrap";
import Header from "components/Headers/Header";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";

// ✅ BASE จาก env
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const API_REPORTS = `${API_BASE}/reports`;
const apiCustomerReportsUrl = (customerId) =>
  `${API_BASE}/customers/${encodeURIComponent(String(customerId))}/reports`;

const AddReportCustomer = () => {
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const location = useLocation();
  const customer = location.state?.customer;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [reports, setReports] = useState([]);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [search, setSearch] = useState("");
  const [itemMap, setItemMap] = useState({});

  const customerId =
    customer?.customerId ||
    customer?.id ||
    customer?.cmId ||
    customer?.customer_id ||
    customer?.cmCode;

  const customerName = customer?.customerName ?? customer?.cmName ?? "-";

  const loadReports = async () => {
    try {
      if (!API_BASE) throw new Error("REACT_APP_API_BASE is not set.");
      setLoading(true);
      setErr("");

      const res = await axios.get(API_REPORTS, { timeout: 15000 });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setReports(list);
    } catch (e) {
      setReports([]);
      setErr(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const k = (search || "").toLowerCase().trim();
    if (!k) return reports;
    return reports.filter((r) => {
      const name = (r.reportName || "").toLowerCase();
      const path = (r.reportPath || "").toLowerCase();
      return name.includes(k) || path.includes(k);
    });
  }, [reports, search]);

  const togglePick = (id, on) => {
    setSelectedReportIds((prev) => {
      const set = new Set(prev);
      if (on) set.add(id);
      else set.delete(id);
      return [...set];
    });

    if (on) {
      const r = reports.find((x) => (x.reportId || x.id) === id);
      setItemMap((prev) => {
        if (prev[id]) return prev;
        const nextSortOrder = (Object.keys(prev).length + 1) * 10;
        return {
          ...prev,
          [id]: {
            reportId: id,
            menuName: r?.reportName || "",
            sortOrder: nextSortOrder,
            isActive: true,
            overrideJson: undefined,
          },
        };
      });
    } else {
      setItemMap((prev) => {
        const clone = { ...prev };
        delete clone[id];
        return clone;
      });
    }
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();

    if (!customerId) {
      await MySwal.fire({
        icon: "error",
        title: "CustomerId not found.",
        text: "Please go back and select a new Customer.",
      });
      return;
    }

    if (selectedReportIds.length === 0) {
      await MySwal.fire({
        icon: "warning",
        title: "Please select Report.",
        text: "At least 1 item",
      });
      return;
    }

    const items = selectedReportIds.map((rid, idx) => {
      const cfg = itemMap[rid] || {};
      const r = reports.find((x) => (x.reportId || x.id) === rid);

      const menuName = cfg.menuName ?? r?.reportName ?? "";
      const sortOrder = Number.isFinite(Number(cfg.sortOrder))
        ? Number(cfg.sortOrder)
        : (idx + 1) * 10;

      const titleText = `${menuName}${
        customerName && customerName !== "-" ? ` - ${customerName}` : ""
      }`;

      const overrideJson = JSON.stringify({ title: titleText });

      return {
        reportId: rid,
        menuName,
        sortOrder,
        isActive: cfg.isActive ?? true,
        overrideJson,
      };
    });

    try {
      if (!API_BASE) throw new Error("REACT_APP_API_BASE is not set.");
      setLoading(true);
      setErr("");

      const url = apiCustomerReportsUrl(customerId);
      const payload = { items };

      await axios.post(url, payload, { timeout: 15000 });

      await MySwal.fire({
        icon: "success",
        title: "Saved successfully",
        text: `Select ${items.length} item`,
        timer: 1200,
        showConfirmButton: false,
      });

      navigate("/customer-report");
    } catch (e2) {
      const msg =
        e2?.response?.data?.message || e2?.message;
      setErr(msg);
      await MySwal.fire({
        icon: "error",
        title: "Failed to save",
        text: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const allIds = useMemo(
    () => filtered.map((r) => r.reportId || r.id),
    [filtered]
  );

  const isAllSelected =
    allIds.length > 0 && allIds.every((id) => selectedReportIds.includes(id));

  const selectAll = () => {
    setSelectedReportIds((prev) => {
      const set = new Set(prev);
      allIds.forEach((id) => set.add(id));
      return [...set];
    });

    setItemMap((prev) => {
      const clone = { ...prev };
      allIds.forEach((id, idx) => {
        if (!clone[id]) {
          const r = reports.find((x) => (x.reportId || x.id) === id);
          clone[id] = {
            reportId: id,
            menuName: r?.reportName || "",
            sortOrder: (idx + 1) * 10,
            isActive: true,
            overrideJson: undefined,
          };
        }
      });
      return clone;
    });
  };

  const clearAll = () => {
    setSelectedReportIds([]);
    setItemMap({});
  };

  const toggleSelectAll = (checked) => {
    if (checked) selectAll();
    else clearAll();
  };

  return (
    <>
      <style>{`
        .zi-pill{
          display:inline-flex; align-items:center; width:fit-content;
          padding:6px 10px; border-radius:999px; font-size:12px; font-weight:800;
          border:1px solid rgba(15,23,42,.10); background:#fff; color:#0f172a;
        }
        .zi-pill.on{
          background: rgba(45,206,137,.12);
          border-color: rgba(45,206,137,.22);
          color:#0f5132;
        }
        .zi-pill.off{
          background: rgba(245,54,92,.10);
          border-color: rgba(245,54,92,.18);
          color:#7a1026;
        }

        .zi-table-wrap{
          background:#fff;
          border-radius:10px;
          border:1px solid rgba(0,0,0,.06);
          overflow:hidden;
        }
        .zi-table{ margin:0; }
        .zi-table thead th{
          background:#f7fafc;
          color:#0f172a;
          font-weight:700;
          font-size:14px;
          border-bottom:1px solid rgba(0,0,0,.06);
          padding:14px 16px;
          white-space:nowrap;
        }
        .zi-table tbody td{
          padding:14px 16px;
          border-top:1px solid rgba(0,0,0,.06);
          vertical-align:middle;
          font-size:14px;
          color:#0f172a;
        }
        .zi-table tbody tr:hover{ background:#f9fbff; }
        .fw-600{ font-weight:600; }
        .gap-2{ gap:8px; }

        /* ✅ FIX: checkbox column alignment */
        .zi-check-col{
          width:56px;
          padding:0 !important;          /* override 14px 16px */
          text-align:center;
        }
        .zi-check-wrap{
          display:flex;
          align-items:center;
          justify-content:center;
          height:100%;
          min-height:44px;
        }
        .zi-checkbox{
          width:18px;
          height:18px;
          margin:0;                      /* สำคัญมาก */
          cursor:pointer;
          accent-color:#2dce89;          /* ถ้าไม่อยากกำหนดสี ลบได้ */
        }
      `}</style>

      <Header />
      <Container className="mt--7" fluid>
        <Row className="justify-content-center">
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center w-100">
                  <Col xs="12">
                    <h6 className="mb-0">Add Report</h6>
                    <div className="text-muted small mt-1">
                      Customer: {customerName}
                    </div>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form onSubmit={onSubmit}>
                  <Row className="mb-3">
                    <Col md="6">
                      <FormGroup className="mb-0">
                        <Label className="form-control-label">Search</Label>
                        <Input
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Report Name/Path"
                        />
                      </FormGroup>
                    </Col>

                    <Col
                      md="6"
                      className="d-flex align-items-end justify-content-md-end mt-2 mt-md-0"
                    >
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <div className="text-muted small">
                          Selected: {selectedReportIds.length}
                        </div>

                        <Button size="sm" color="primary" outline onClick={selectAll}>
                          Select All
                        </Button>

                        <Button size="sm" color="secondary" outline onClick={clearAll}>
                          Clear All
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {loading ? (
                    <div className="text-muted">กำลังโหลดรายงาน…</div>
                  ) : err ? (
                    <div className="text-danger">{err}</div>
                  ) : filtered.length === 0 ? (
                    <div className="text-muted">ยังไม่มีรายงานในระบบ</div>
                  ) : (
                    <div className="zi-table-wrap">
                      <Table responsive hover className="zi-table mb-0">
                        <thead>
                          <tr>
                            <th className="zi-check-col">
                              <div className="zi-check-wrap">
                                <input
                                  type="checkbox"
                                  className="zi-checkbox"
                                  checked={isAllSelected}
                                  onChange={(e) => toggleSelectAll(e.target.checked)}
                                  aria-label="Select all"
                                />
                              </div>
                            </th>
                            <th className="text-start">Report Name</th>
                            <th className="text-start">Path</th>
                            <th style={{ width: 160 }} className="text-start">
                              Export
                            </th>
                            <th style={{ width: 140 }} className="text-start">
                              Active
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {filtered.map((r) => {
                            const id = r.reportId || r.id;
                            const checked = selectedReportIds.includes(id);

                            return (
                              <tr key={id}>
                                <td className="zi-check-col">
                                  <div className="zi-check-wrap">
                                    <input
                                      type="checkbox"
                                      className="zi-checkbox"
                                      checked={checked}
                                      onChange={(e) => togglePick(id, e.target.checked)}
                                      aria-label={`Select ${r.reportName || "report"}`}
                                    />
                                  </div>
                                </td>

                                <td className="fw-600">{r.reportName || "-"}</td>
                                <td className="text-muted">{r.reportPath || "-"}</td>
                                <td>{r.exportModes || "-"}</td>
                                <td>
                                  <span className={`zi-pill ${r.isActive ? "on" : "off"}`}>
                                    {r.isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  <button type="submit" style={{ display: "none" }} />
                </Form>

                <div className="d-flex justify-content-end mt-4">
                  <div className="d-flex gap-2">
                    <Button color="secondary" onClick={() => navigate(-1)}>
                      Back
                    </Button>
                    <Button
                      color="primary"
                      type="button"
                      onClick={onSubmit}
                      disabled={loading}
                      style={{ minWidth: 120, fontWeight: 700 }}
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddReportCustomer;